import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Typography, Button, Card, Descriptions, Tag, Space, Divider, message, Modal } from 'antd';
import { EditOutlined, ArrowLeftOutlined, DeleteOutlined, PoweroffOutlined, CheckCircleOutlined, DownloadOutlined } from '@ant-design/icons';

import { useTermsAndConditions } from '../hooks/useTermsAndConditions';
import { useTermsAndConditionsStore } from '../stores/termsAndConditionsStore';
import {  ContentFormat } from '../types';

const { Title, Paragraph } = Typography;

export const DetailView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const { currentItem, setCurrentItem } = useTermsAndConditionsStore();
  
  const { 
    getById, 
    termsById,
    loading,
    activateTermsAndConditions,
    deactivateTermsAndConditions,
    deleteTermsAndConditions
  } = useTermsAndConditions();

  // Fetch the terms and conditions when the component mounts or id changes
  useEffect(() => {
    if (!id) {
      navigate('/terms-and-conditions');
      return;
    }

    // If we already have the item in the store, use it
    if (currentItem && currentItem.id === id) {
      // No need to fetch, we'll use the store data
    } else {
      // Otherwise fetch it from the API
      getById(id);
    }
  }, [id, currentItem, getById, navigate]);

  // Update the store when we get new data from the API
  useEffect(() => {
    if (termsById) {
      setCurrentItem(termsById);
    }
  }, [termsById, setCurrentItem]);

  // Use either the store data or the API data
  const termsAndConditions = currentItem?.id === id ? currentItem : termsById;

  const handleEdit = () => {
    navigate(`/terms-and-conditions/${id}/edit`);
  };

  const handleBack = () => {
    navigate('/terms-and-conditions');
  };

  const handleDelete = () => {
    Modal.confirm({
      title: 'Are you sure you want to delete these terms and conditions?',
      content: 'This action cannot be undone.',
      okText: 'Yes, Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        if (id) {
          const success = await deleteTermsAndConditions(id);
          if (success) {
            message.success('Terms and conditions deleted successfully');
            navigate('/terms-and-conditions');
          }
        }
      }
    });
  };

  const handleActivate = async () => {
    if (id) {
      const success = await activateTermsAndConditions(id);
      if (success) {
        message.success('Terms and conditions activated successfully');
      }
    }
  };

  const handleDeactivate = async () => {
    if (id) {
      const success = await deactivateTermsAndConditions(id);
      if (success) {
        message.success('Terms and conditions deactivated successfully');
      }
    }
  };

  const handleDownload = () => {
    if (termsAndConditions?.fileId) {
      window.open(`/api/files/download/${termsAndConditions.fileId}`, '_blank');
    }
  };

  const renderContent = () => {
    if (!termsAndConditions?.content) {
      return <Paragraph>No content available.</Paragraph>;
    }

    switch (termsAndConditions.contentFormat) {
      case ContentFormat.HTML:
        return (
          <div 
            className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700"
            dangerouslySetInnerHTML={{ __html: termsAndConditions.content }}
          />
        );
      case ContentFormat.MARKDOWN:
        return (
          <div className="bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
            <Paragraph className="whitespace-pre-line mb-0">
              {termsAndConditions.content}
            </Paragraph>
          </div>
        );
      case ContentFormat.PLAIN_TEXT:
      default:
        return (
          <Paragraph className="whitespace-pre-line bg-white dark:bg-gray-800 p-4 rounded border border-gray-200 dark:border-gray-700">
            {termsAndConditions.content}
          </Paragraph>
        );
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={handleBack}
          >
            Back to List
          </Button>
          <Title level={3} className="m-0">
            Terms and Conditions Details
          </Title>
        </Space>
        <Space>
          {termsAndConditions?.active ? (
            <Button 
              icon={<PoweroffOutlined />} 
              danger
              onClick={handleDeactivate}
              disabled={loading}
            >
              Deactivate
            </Button>
          ) : (
            <Button 
              icon={<CheckCircleOutlined />} 
              type="primary"
              onClick={handleActivate}
              disabled={loading}
            >
              Activate
            </Button>
          )}
          <Button 
            icon={<EditOutlined />} 
            onClick={handleEdit}
            disabled={loading}
          >
            Edit
          </Button>
          <Button 
            icon={<DeleteOutlined />} 
            danger
            onClick={handleDelete}
            disabled={loading}
          >
            Delete
          </Button>
        </Space>
      </div>

      <Card loading={loading}>
        {termsAndConditions && (
          <>
            <Descriptions 
              title={termsAndConditions.title} 
              bordered 
              column={{ xs: 1, sm: 2, md: 3 }}
            >
              <Descriptions.Item label="Type">
                {termsAndConditions.type?.replace(/_/g, ' ')}
              </Descriptions.Item>
              <Descriptions.Item label="Entity ID">
                {termsAndConditions.entityId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Version">
                {termsAndConditions.version}
              </Descriptions.Item>
              <Descriptions.Item label="Format">
                {termsAndConditions.contentFormat?.replace(/_/g, ' ') || 'HTML'}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                {termsAndConditions.active ? 
                  <Tag color="green">Active</Tag> : 
                  <Tag color="orange">Inactive</Tag>
                }
              </Descriptions.Item>
              <Descriptions.Item label="Created Date">
                {termsAndConditions.madeAt ?
                  new Date(termsAndConditions.madeAt).toLocaleString() :
                  '-'
                }
              </Descriptions.Item>
              <Descriptions.Item label="Last Updated">
                {termsAndConditions.modifiedAt ?
                  new Date(termsAndConditions.modifiedAt).toLocaleString() :
                  '-'
                }
              </Descriptions.Item>
              {termsAndConditions.description && (
                <Descriptions.Item label="Description" span={3}>
                  {termsAndConditions.description}
                </Descriptions.Item>
              )}
            </Descriptions>

            {termsAndConditions.fileId && (
              <div className="mt-4">
                <Button 
                  type="primary" 
                  icon={<DownloadOutlined />}
                  onClick={handleDownload}
                >
                  Download Document
                </Button>
              </div>
            )}

            <Divider orientation="left">Content</Divider>
            {renderContent()}
          </>
        )}
      </Card>
    </div>
  );
};
